(() => {
  /** @type {Window & {__amBackInStockInitialized?: boolean, __amBackInStockObserverInitialized?: boolean}} */
  const win = window;
  
  if (win.__amBackInStockInitialized) return;
  win.__amBackInStockInitialized = true;

  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * @param {Document | HTMLElement} [context]
   * @returns {HTMLElement[]}
   */
  const getRoots = (context) => {
    const ctx = context || document;
    return Array.from(ctx.querySelectorAll('[data-back-in-stock-root]'));
  };

  /**
   * @param {HTMLElement} root
   */
  const getElements = (root) => {
    const wrapper = root.closest('.add-to-cart-wrapper');
    const formButtons = root.closest('.product-form-buttons');
    
    return {
      addToCartWrapper: /** @type {HTMLElement | null} */ (wrapper ? wrapper.querySelector('[data-back-in-stock-add-to-cart]') : null),
      quantitySelector: /** @type {HTMLElement | null} */ (formButtons ? formButtons.querySelector('.quantity-selector') : null),
      dialogComponent: /** @type {any} */ (root.querySelector('dialog-component')),
      trigger: /** @type {HTMLElement | null} */ (root.querySelector('[data-back-in-stock-trigger]')),
      form: /** @type {HTMLElement | null} */ (root.querySelector('[data-back-in-stock-form]')),
      email: /** @type {HTMLInputElement | null} */ (root.querySelector('[data-back-in-stock-email]')),
      submit: /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-back-in-stock-submit]')),
      validation: /** @type {HTMLElement | null} */ (root.querySelector('[data-back-in-stock-validation]')),
      success: /** @type {HTMLElement | null} */ (root.querySelector('[data-back-in-stock-success]')),
      resend: /** @type {HTMLElement | null} */ (root.querySelector('[data-back-in-stock-resend]')),
      closeButtons: /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-back-in-stock-close]'))),
    };
  };

  /**
   * @param {HTMLElement} root
   * @param {boolean} isVisible
   */
  const syncVisibility = (root, isVisible) => {
    const { addToCartWrapper, quantitySelector } = getElements(root);
    root.hidden = !isVisible;
    if (addToCartWrapper) {
      addToCartWrapper.hidden = isVisible;
    }
    if (quantitySelector) {
      quantitySelector.hidden = isVisible;
    }
  };

  /**
   * @param {HTMLElement} root
   */
  const resetState = (root) => {
    const { email, form, submit, success, resend, validation } = getElements(root);
    if (email) email.classList.remove('back-in-stock__input--invalid');
    if (form) form.hidden = false;
    if (submit) submit.disabled = false;
    if (success) success.hidden = true;
    if (resend) resend.hidden = true;
    if (validation) validation.hidden = true;
  };

  /**
   * @param {HTMLElement} root
   * @param {{id?: number | string, available?: boolean} | null} variant
   */
  const setVariant = (root, variant) => {
    if (variant && variant.id) {
      root.dataset.variantId = String(variant.id);
    }

    const shouldShow = variant !== null && Boolean(variant.id) && variant.available === false;
    syncVisibility(root, shouldShow);
  };

  /**
   * @param {HTMLElement} root
   */
  const showValidationError = (root) => {
    const { email, validation } = getElements(root);
    if (email) email.classList.add('back-in-stock__input--invalid');
    if (validation) validation.hidden = false;
  };

  /**
   * @param {HTMLElement} root
   */
  const clearValidationError = (root) => {
    const { email, validation } = getElements(root);
    if (email) email.classList.remove('back-in-stock__input--invalid');
    if (validation) validation.hidden = true;
  };

  /**
   * @param {HTMLElement} root
   */
  const submitSubscription = async (root) => {
    const { email, form, submit, success, resend } = getElements(root);
    const companyId = root.dataset.companyId;
    const variantId = root.dataset.variantId;
    const value = email && email.value ? email.value.trim() : '';

    if (!companyId || !variantId || !email || !submit) return;

    if (!EMAIL_PATTERN.test(value)) {
      showValidationError(root);
      return;
    }

    clearValidationError(root);
    submit.disabled = true;

    try {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: value,
                },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-06-15',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Klaviyo BIS request failed with status ${response.status}`);
      }

      if (form) form.hidden = true;
      if (success) success.hidden = false;
      if (resend) resend.hidden = false;
    } catch (error) {
      console.error('[back-in-stock]', error);
    } finally {
      submit.disabled = false;
    }
  };

  /**
   * @param {HTMLElement} root
   */
  const initRoot = (root) => {
    if (root.dataset.backInStockBound === 'true') return;
    root.dataset.backInStockBound = 'true';

    const { dialogComponent, trigger, submit, resend, email, closeButtons } = getElements(root);

    if (trigger) {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!root.dataset.companyId) return;
        resetState(root);
        if (dialogComponent && dialogComponent.showDialog) {
          dialogComponent.showDialog();
        }
      });
    }

    if (submit) {
      submit.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        submitSubscription(root);
      });
    }

    if (resend) {
      resend.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        resetState(root);
        if (email) email.focus();
      });
    }

    if (email) {
      email.addEventListener('input', () => {
        clearValidationError(root);
      });
    }

    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (dialogComponent && dialogComponent.closeDialog) {
          dialogComponent.closeDialog();
        }
      });
    });
  };

  /**
   * @param {Document | HTMLElement} [context]
   */
  const initAll = (context) => {
    getRoots(context).forEach((root) => {
      initRoot(root);
      syncVisibility(root, !root.hidden);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target && target instanceof HTMLElement) {
      initAll(target);
    }
  });

  ['collection:updated', 'product-grid:updated', 'facets:updated'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        initAll(target);
        return;
      }
      initAll();
    });
  });

  if (!win.__amBackInStockObserverInitialized) {
    win.__amBackInStockObserverInitialized = true;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (
            node.matches?.('[data-back-in-stock-root]') ||
            node.querySelector?.('[data-back-in-stock-root]')
          ) {
            initAll(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener('variant:update', (event) => {
    const target = event.target;
    /** @type {Document | HTMLElement} */
    let scope = document;
    
    if (target instanceof HTMLElement) {
      const closest = target.closest('.shopify-section, dialog, product-card');
      scope = closest instanceof HTMLElement ? closest : target;
    }
    
    getRoots(scope).forEach((root) => {
      initRoot(root);
      /** @type {any} */
      const customEvent = event;
      const variant = customEvent.detail && customEvent.detail.resource ? customEvent.detail.resource : null;
      setVariant(root, variant);
    });
  });
})();
